'use client';

import Image from 'next/image';

import { useTranslation } from '../hooks/useTranslation';

const PRIZES_CDN_BASE = 'https://ocnc-fantasy.s3.eu-west-1.amazonaws.com/prizes/cdm';

const endPrizeRows: { placeKey: string; titleKey?: string; descKey: string; imageSrc: string }[] = [
  { placeKey: 'prizesPage.place1', titleKey: 'prizesPage.endPrize1Title', descKey: 'prizesPage.wc26TripFull', imageSrc: `${PRIZES_CDN_BASE}/battlekart.jpg` },
  { placeKey: 'prizesPage.place2', titleKey: 'prizesPage.endPrize2Title', descKey: 'prizesPage.subPremiumFull', imageSrc: `${PRIZES_CDN_BASE}/natura-parc.jpg` },
  { placeKey: 'prizesPage.place3', titleKey: 'prizesPage.endPrize3Title', descKey: 'prizesPage.subRegularFull', imageSrc: `${PRIZES_CDN_BASE}/flyinn-liege.jpg` },
  { placeKey: 'prizesPage.place4', titleKey: 'prizesPage.endPrize4Title', descKey: 'prizesPage.endPrize4Full', imageSrc: `${PRIZES_CDN_BASE}/skydive-spa.jpg` },
  { placeKey: 'prizesPage.place5', titleKey: 'prizesPage.endPrize5Title', descKey: 'prizesPage.endPrize5Full', imageSrc: `${PRIZES_CDN_BASE}/grottes-de-remouchamps.jpg` },
  { placeKey: 'prizesPage.place6', titleKey: 'prizesPage.endPrize6Title', descKey: 'prizesPage.endPrize6Full', imageSrc: `${PRIZES_CDN_BASE}/bike-parc.jpg` },
  { placeKey: 'prizesPage.place7', titleKey: 'prizesPage.endPrize7Title', descKey: 'prizesPage.endPrize7Full', imageSrc: `${PRIZES_CDN_BASE}/karting-floreffe.jpg` },
];

function PrizeArticle({
  placeKey,
  titleKey,
  descKey,
  imageSrc,
}: {
  placeKey: string;
  titleKey?: string;
  descKey: string;
  imageSrc: string;
}) {
  const { t } = useTranslation();
  const imageAlt = titleKey ? t(titleKey) : t(placeKey);

  return (
    <article className="overflow-hidden rounded-lg bg-white shadow-[0px_2px_8px_rgba(0,0,0,0.04)]">
      <div className="relative aspect-square w-full bg-[var(--light,#f0f5f9)]">
        <Image
          src={imageSrc}
          alt={imageAlt}
          fill
          className="object-cover"
          sizes="(max-width: 640px) 100vw, 384px"
        />
      </div>
      <div className="space-y-2 p-5">
        {titleKey ? (
          <h3 className="text-[16px] font-semibold text-[var(--rtbf-blue,#0076A9)]">{t(titleKey)}</h3>
        ) : (
          <h3 className="text-[16px] font-semibold text-[var(--rtbf-blue,#0076A9)]">{t(placeKey)}</h3>
        )}
        <div className="space-y-3">
          {t(descKey)
            .split(/\n\n+/)
            .map((paragraph) => paragraph.trim())
            .filter(Boolean)
            .map((paragraph, index) => (
              <p
                key={index}
                className="text-[14px] font-normal leading-relaxed text-[var(--rtbf-dark,#001425)]"
              >
                {paragraph}
              </p>
            ))}
        </div>
      </div>
    </article>
  );
}

export default function PrizesPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-[calc(100vh-100px)] px-3 pb-20 pt-6 sm:px-4">
      <div className="mx-auto w-full max-w-3xl">
        <section className="space-y-8 py-1 sm:py-0">
          <div className="space-y-3">
            <h1 className="text-[22px] font-semibold leading-tight text-white sm:text-[24px]">
              {t('prizesPage.title')}
            </h1>
            <h2 className="text-[18px] font-semibold text-white">{t('prizesPage.endPrizesTitle')}</h2>
            <p className="text-[14px] font-normal leading-relaxed text-white/85">{t('prizesPage.endPrizesIntro')}</p>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {endPrizeRows.map((row) => (
              <PrizeArticle
                key={row.titleKey ?? row.descKey}
                placeKey={row.placeKey}
                titleKey={row.titleKey}
                descKey={row.descKey}
                imageSrc={row.imageSrc}
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
